import { useEffect, useId, useState, type FormEvent } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { usePostHog } from "@posthog/react";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

const SUPPORT_EMAIL = "support@usesill.com";
const SUCCESS_AUTOCLOSE_MS = 1600;

type Props = {
  open: boolean;
  onClose: () => void;
};

type SubmitState = "idle" | "submitting" | "success" | "error";

type PostHogLike = {
  capture?: (event: string, props?: Record<string, unknown>) => unknown;
};

export function FeedbackModal({ open, onClose }: Props) {
  if (!open) return null;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => (next ? null : onClose())}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-foreground/25 backdrop-blur-sm" />
        <DialogPrimitive.Content
          aria-describedby=""
          data-testid="feedback-modal"
          className={cn(
            "fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[2rem] border border-border bg-card p-6 shadow-xl space-y-4",
          )}
        >
          {/* Body is a separate component so its state is reset by unmount when
           *  the modal closes — avoids resetting via a setState-in-effect. */}
          <FeedbackModalBody onClose={onClose} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FeedbackModalBody({ onClose }: { onClose: () => void }) {
  const posthog = usePostHog();
  const surveyId = import.meta.env.VITE_POSTHOG_FEEDBACK_SURVEY_ID as string | undefined;
  const textareaId = useId();
  const [text, setText] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");

  // Fire `survey shown` once on mount so PostHog conversion math is accurate.
  useEffect(() => {
    if (!surveyId || !posthog) return;
    try {
      (posthog as unknown as PostHogLike).capture?.call(
        posthog,
        "survey shown",
        { $survey_id: surveyId },
      );
    } catch {
      // Non-fatal; capture failures should not block the form.
    }
  }, [posthog, surveyId]);

  // Auto-close after success.
  useEffect(() => {
    if (submitState !== "success") return;
    const id = window.setTimeout(onClose, SUCCESS_AUTOCLOSE_MS);
    return () => window.clearTimeout(id);
  }, [submitState, onClose]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || submitState === "submitting") return;
    if (!surveyId || !posthog) {
      setSubmitState("error");
      return;
    }
    setSubmitState("submitting");
    try {
      (posthog as unknown as PostHogLike).capture?.call(
        posthog,
        "survey sent",
        { $survey_id: surveyId, $survey_response: trimmed },
      );
      setSubmitState("success");
    } catch (err) {
      console.error("[feedback] capture failed", err);
      setSubmitState("error");
    }
  };

  const submitDisabled =
    !text.trim() || submitState === "submitting" || submitState === "success";

  return (
    <>
      <DialogPrimitive.Title className="sr-only">
        Send feedback
      </DialogPrimitive.Title>
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Send feedback
          </h2>
          <p className="text-sm text-muted-foreground">
            Tell us what's working, what's broken, or what you wish Sill did.
          </p>
        </div>
        <button
          type="button"
          data-testid="feedback-close"
          aria-label="Close"
          className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {submitState === "success" ? (
        <p
          data-testid="feedback-success"
          className="rounded-[1.25rem] bg-muted px-4 py-3 text-sm text-foreground"
        >
          Thanks — we got it.
        </p>
      ) : (
        <form
          data-testid="feedback-form"
          className="space-y-3"
          onSubmit={handleSubmit}
        >
          <Textarea
            id={textareaId}
            data-testid="feedback-textarea"
            placeholder="What's on your mind?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={submitState === "submitting"}
            aria-label="Feedback"
            rows={5}
          />
          {submitState === "error" && (
            <p className="text-sm text-destructive" data-testid="feedback-error">
              Couldn't send — please email us instead.
            </p>
          )}
          <div className="flex items-center justify-start">
            <Button
              type="submit"
              size="sm"
              disabled={submitDisabled}
              data-testid="feedback-submit"
            >
              {submitState === "submitting" ? "Sending…" : "Send feedback"}
            </Button>
          </div>
        </form>
      )}

      <p className="text-xs text-muted-foreground">
        Prefer email? Reach us at{" "}
        <a
          className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
          href={`mailto:${SUPPORT_EMAIL}`}
        >
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </>
  );
}
